import {
  CatalogueIcon,
  ChevronDownIcon,
  DownloadIcon,
  FarmerSalesIcon,
  RupeeIcon,
  UserManagementIcon,
} from 'core/components/Icons'
import { PageHeader } from 'core/components/ui/PageHeader'
import { StatCard } from '../components/StatCard'
import { FarmerSalesTable } from '../components/salesReport/FarmerSalesTable'
import { FpoFarmerOnboardingTable } from '../components/salesReport/FpoFarmerOnboardingTable'
import { FpoWiseSalesTable } from '../components/salesReport/FpoWiseSalesTable'
import { InventoryStatusTable } from '../components/salesReport/InventoryStatusTable'
import { MoneyRecoveredTable } from '../components/salesReport/MoneyRecoveredTable'
import { PresenceTable } from '../components/salesReport/PresenceTable'
import { PrimarySalesPanel } from '../components/salesReport/PrimarySalesPanel'
import { formatCurrency, formatMT } from '../components/salesReport/format'
import { useSalesReport } from '../hooks/useSalesReport'

import axiosInstance from 'core/network/axiosInstance'
import { useEffect, useRef, useState } from 'react'

const ReportDownloadButton = ({
  label,
  endpointBase,
  downloading,
  onDownload,
  primary = false,
}: {
  label: string
  endpointBase: string
  downloading: boolean
  onDownload: (endpoint: string, filename: string) => void
  primary?: boolean
}) => {
  const [open, setOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelect = (format: 'csv' | 'xlsx' | 'pdf') => {
    setOpen(false)
    const ext = format
    const filename = `${label.toLowerCase().replace(' ', '_')}.${ext}`
    onDownload(`${endpointBase}?format=${format}`, filename)
  }

  return (
    <div className="relative inline-block text-left" ref={menuRef}>
      <button
        onClick={() => setOpen(!open)}
        disabled={downloading}
        className={`px-3 py-1.5 text-xs font-medium rounded-btn disabled:opacity-50 transition-colors flex items-center ${
          primary
            ? 'bg-primary hover:bg-primary-hover text-primary-foreground'
            : 'border border-border bg-surface hover:bg-bg text-text-primary'
        }`}
      >
        <DownloadIcon size={14} className="mr-1.5" />
        {label}
        <ChevronDownIcon size={14} className="ml-1.5 opacity-70" />
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-32 rounded-card shadow-lg bg-surface border border-border ring-1 ring-black ring-opacity-5 focus:outline-none z-10 overflow-hidden">
          <div className="py-1">
            <button
              onClick={() => handleSelect('csv')}
              className="w-full text-left block px-4 py-2 text-xs text-text-primary hover:bg-bg transition-colors"
            >
              CSV format
            </button>
            <button
              onClick={() => handleSelect('xlsx')}
              className="w-full text-left block px-4 py-2 text-xs text-text-primary hover:bg-bg transition-colors"
            >
              Excel (.xlsx)
            </button>
            <button
              onClick={() => handleSelect('pdf')}
              className="w-full text-left block px-4 py-2 text-xs text-text-primary hover:bg-bg transition-colors"
            >
              PDF Report
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export const DashboardPage = () => {
  const { data, loading, error, refresh } = useSalesReport()
  const [downloading, setDownloading] = useState(false)

  const handleDownload = async (endpoint: string, filename: string) => {
    try {
      setDownloading(true)
      const response = await axiosInstance.get(endpoint, {
        responseType: 'blob',
      })

      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      link.parentNode?.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      // NOSONAR: Logging download failure for client diagnostics
      // eslint-disable-next-line no-console
      console.error('Download failed', err) // NOSONAR
      alert('Failed to download report')
    } finally {
      setDownloading(false)
    }
  }

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 2xl:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-28 rounded-card bg-surface border border-border" />
          ))}
        </div>
        <div className="h-[280px] rounded-card bg-surface border border-border" />
        <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-2">
          <div className="h-72 rounded-card bg-surface border border-border" />
          <div className="h-72 rounded-card bg-surface border border-border" />
        </div>
        <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-2">
          <div className="h-72 rounded-card bg-surface border border-border" />
          <div className="h-72 rounded-card bg-surface border border-border" />
        </div>
        <div className="h-56 rounded-card bg-surface border border-border" />
        <div className="h-96 rounded-card bg-surface border border-border" />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="rounded-card border border-danger/20 bg-danger/5 p-8 text-center">
        <h3 className="text-lg font-bold text-danger">Error Loading Sales Report</h3>
        <p className="mt-2 text-sm text-text-secondary">{error || 'An unexpected error occurred.'}</p>
        <button
          onClick={() => refresh()}
          className="mt-4 rounded-btn bg-danger px-4 py-2 text-xs font-bold text-white hover:bg-danger/90"
        >
          Try Again
        </button>
      </div>
    )
  }

  const grossMargin = (data.fpoWiseSales?.totalAmount || 0) - (data.fpoWiseSales?.cogs || 0)

  return (
    <div className="space-y-6">
      <PageHeader
        title={data.reportTitle}
        description={`Reporting period: ${data.periodLabel}`}
        actions={
          <div className="flex gap-2">
            <ReportDownloadButton
              label="Sales Report"
              endpointBase="/api/v1/reports/sales"
              downloading={downloading}
              onDownload={handleDownload}
            />
            <ReportDownloadButton
              label="FPO Detail"
              endpointBase="/api/v1/reports/fpo-detail"
              downloading={downloading}
              onDownload={handleDownload}
            />
            <ReportDownloadButton
              label="Unit Tracker"
              endpointBase="/api/v1/reports/unit-tracker"
              downloading={downloading}
              onDownload={handleDownload}
              primary={true}
            />
          </div>
        }
      />

      {/* KPI Strip */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 2xl:grid-cols-4">
        <StatCard
          title="Monthly Volume"
          value={`${formatMT(data.primarySales.ytdVolumeMt)} MT`}
          trendLabel="this month"
          icon={<CatalogueIcon size={20} />}
          iconBg="bg-status-active/10"
          iconColor="text-status-active"
          sparklinePoints={data.primarySales.monthly.map((m) => m.volumeMt)}
          sparklineStroke="var(--ft-status-active)"
        />
        <StatCard
          title="Monthly Revenue"
          value={`₹ ${data.primarySales.ytdRevenueLakhs.toFixed(2)} L`}
          trendLabel="this month"
          icon={<RupeeIcon size={20} />}
          iconBg="bg-primary/10"
          iconColor="text-primary"
          sparklinePoints={data.primarySales.monthly.map((m) => m.revenueLakhs)}
          sparklineStroke="var(--ft-primary)"
        />
        <StatCard
          title="Farmers Reached"
          value={data.farmersReached}
          trendLabel="across all FPOs"
          icon={<UserManagementIcon size={20} />}
          iconBg="bg-status-info/10"
          iconColor="text-status-info"
          sparklinePoints={
            data.presence.length > 0
              ? data.presence.map((p) => p.farmers)
              : data.fpoFarmerOnboarding.map((o) => o.totalFarmers)
          }
          sparklineStroke="var(--ft-status-info)"
        />
        <StatCard
          title="Gross Margin"
          value={formatCurrency(grossMargin)}
          trendLabel="FPO wise sales, this period"
          icon={<FarmerSalesIcon size={20} />}
          iconBg="bg-secondary/10"
          iconColor="text-secondary"
          sparklinePoints={data.fpoWiseSales.transactions.slice(-7).map((t) => t.amount)}
          sparklineStroke="var(--ft-secondary)"
        />
      </div>

      <PrimarySalesPanel data={data.primarySales} />

      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-2">
        <FarmerSalesTable rows={data.farmerSales} />
        <MoneyRecoveredTable rows={data.moneyRecovered} />
      </div>

      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-2">
        <InventoryStatusTable rows={data.inventoryStatus} />
        <FpoFarmerOnboardingTable rows={data.fpoFarmerOnboarding} />
      </div>

      <PresenceTable rows={data.presence} />

      <FpoWiseSalesTable data={data.fpoWiseSales} />
    </div>
  )
}
